/**
 * Contrato del journey de pacientes (overview `/patients` + cards US-042 / #33).
 *
 * Conteos del periodo (snapshots), no tasas de cohorte entre etapas.
 * Ver docs/PATIENT_JOURNEY_CONTRACT.md
 */

import type {
  PacientesMetrics,
  PacientesNoVolvieronResumen,
  PacientesRecurrencia,
} from './metricsTypes';

export const PATIENT_JOURNEY_STAGE_IDS = [
  'nuevo',
  'regular',
  'en_riesgo',
  'no_volvio',
] as const;

export type PatientJourneyStageId = (typeof PATIENT_JOURNEY_STAGE_IDS)[number];

export type PatientJourneyDeepLinkTab = 'acquisition' | 'retention' | 'risk';

export type PatientJourneySourceStatus = 'ok' | 'missing' | 'forbidden';

export interface PatientJourneyStage {
  id: PatientJourneyStageId;
  count: number | null;
  ratePct?: number | null;
  available: boolean;
  definitionKey: string;
  deepLinkTab: PatientJourneyDeepLinkTab | null;
}

export interface PatientJourneySnapshot {
  tenantId: string;
  period: string;
  /** Journey es clínica; recurrencia / no-volvieron no scopan por sede. */
  consultorioId: number | null;
  stages: PatientJourneyStage[];
  source: {
    pacientes: PatientJourneySourceStatus;
    recurrencia: PatientJourneySourceStatus;
    noVolvieron: PatientJourneySourceStatus;
  };
}

export interface BuildPatientJourneyInput {
  tenantId: string;
  period: string;
  pacientes: PacientesMetrics | null;
  recurrencia: PacientesRecurrencia | null;
  noVolvieron: PacientesNoVolvieronResumen | null;
  canPacientes: boolean;
  canRecurrencia: boolean;
  canNoVolvieron: boolean;
}

function sourceStatus(can: boolean, data: unknown): PatientJourneySourceStatus {
  if (!can) return 'forbidden';
  if (data == null) return 'missing';
  return 'ok';
}

function stage(
  id: PatientJourneyStageId,
  count: number | null,
  available: boolean,
  deepLinkTab: PatientJourneyDeepLinkTab | null,
  ratePct?: number | null
): PatientJourneyStage {
  return {
    id,
    count: available ? count : null,
    ratePct: available ? ratePct ?? null : null,
    available,
    definitionKey: `domain.patients.journey.stages.${id}.definition`,
    deepLinkTab,
  };
}

/**
 * Mapea las 3 APIs existentes a un snapshot estable para UI y cards mobile.
 * Siempre devuelve las 4 etapas; `en_riesgo` queda `available: false` (v1 sin split
 * de API: el count vive en `no_volvio`). `available: false` = ocultar o mostrar "—".
 */
export function buildPatientJourneySnapshot(input: BuildPatientJourneyInput): PatientJourneySnapshot {
  const { tenantId, period, pacientes, recurrencia, noVolvieron } = input;
  const pacientesOk = input.canPacientes && pacientes != null;
  const recurrenciaOk = input.canRecurrencia && recurrencia != null;
  const riskOk = input.canNoVolvieron && noVolvieron != null;
  const riskTotal = noVolvieron?.total ?? null;

  return {
    tenantId,
    period,
    consultorioId: null,
    source: {
      pacientes: sourceStatus(input.canPacientes, pacientes),
      recurrencia: sourceStatus(input.canRecurrencia, recurrencia),
      noVolvieron: sourceStatus(input.canNoVolvieron, noVolvieron),
    },
    stages: [
      stage('nuevo', pacientes?.nuevos ?? null, pacientesOk, 'acquisition'),
      stage(
        'regular',
        recurrencia?.recurrentes ?? null,
        recurrenciaOk,
        'retention',
        recurrenciaOk ? recurrencia.tasaRecurrencia : null
      ),
      stage('en_riesgo', riskTotal, false, 'risk'),
      stage('no_volvio', riskTotal, riskOk, 'risk'),
    ],
  };
}

export function visiblePatientJourneyStages(snapshot: PatientJourneySnapshot): PatientJourneyStage[] {
  return snapshot.stages.filter((item) => item.available);
}

/** Clínica nueva / sin movimiento: etapas visibles en 0, sin NaN. */
export function isPatientJourneyEmpty(snapshot: PatientJourneySnapshot): boolean {
  const visible = visiblePatientJourneyStages(snapshot);
  if (visible.length === 0) return true;
  return visible.every((item) => (item.count ?? 0) === 0);
}
